import { useEffect, useState } from 'react'
import { palette } from '@versports/theme'
import {
  know,
  type RankingDto,
  type RankingMe,
  type RankingPeriod,
} from '../../api/know'

/* -------------------------------------------------------------------------- */
/* Color helpers — los colores de medalla se DERIVAN de la paleta activa       */
/* (`@versports/theme`), no hay hexes de marca hardcodeados. Mismo cálculo que */
/* `vr-app/src/ui/Leaderboard.tsx`, para que el ranking de la web y el del     */
/* lobby VR se lean como la misma pieza.                                       */
/* -------------------------------------------------------------------------- */

function parseHex(hex: string): [number, number, number] {
  const h = hex.replace('#', '')
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h.slice(0, 6)
  const n = parseInt(full, 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function toHex(rgb: [number, number, number]): string {
  return (
    '#' +
    rgb
      .map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0'))
      .join('')
  )
}

/** Mezcla lineal de dos colores. `t = 0` devuelve `a`, `t = 1` devuelve `b`. */
function mix(a: string, b: string, t: number): string {
  const [ar, ag, ab] = parseHex(a)
  const [br, bg, bb] = parseHex(b)
  return toHex([ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t])
}

/** Alpha como sufijo hex (`#RRGGBBAA`), que es lo que entienden los estilos DOM. */
function alpha(hex: string, a: number): string {
  return hex + Math.round(Math.min(1, Math.max(0, a)) * 255).toString(16).padStart(2, '0')
}

/** Oro / plata / bronce: `warning`, `textSecondary` y una mezcla warning×danger. */
const MEDALS = [
  mix(palette.warning, palette.textPrimary, 0.18),
  mix(palette.textSecondary, palette.textPrimary, 0.3),
  mix(mix(palette.warning, palette.danger, 0.5), palette.surface, 0.28),
]

/**
 * Accent del jugador: el color del club cuando `AuthContext` lo publicó como
 * `--club-primary`, y si no el `primary` del theme. Va por `var()` en vez de
 * por clase Tailwind porque también se usa en tintes y bordes con alpha.
 */
const CLUB = `var(--club-primary, ${palette.primary})`
const clubTint = (a: number) =>
  `color-mix(in srgb, ${CLUB} ${Math.round(a * 100)}%, transparent)`

/**
 * Grilla compartida por la cabecera y TODAS las filas: columnas fijas para los
 * números, así el podio y la banda del 4–10 quedan alineados aunque tengan
 * alturas distintas. `minmax(0,1fr)` en el nombre es lo que habilita el
 * ellipsis en vez de que la fila crezca. Una sola columna en cualquier ancho:
 * en mobile se achica, no se apila.
 */
const GRID =
  'grid grid-cols-[1.9rem_minmax(0,1fr)_2.4rem_2.4rem_3.3rem] items-center gap-x-sm'

const HEAD = 'text-xs font-medium uppercase tracking-wide text-text-muted whitespace-nowrap'

const NUM = 'text-right font-mono text-sm tabular-nums whitespace-nowrap'

const PERIOD_OPTIONS: { value: RankingPeriod; label: string }[] = [
  { value: 'all_time', label: 'Todos' },
  { value: 'weekly', label: 'Semana' },
  { value: 'monthly', label: 'Mes' },
]

/**
 * Ranking global de `/data`, con el mismo lenguaje visual que el leaderboard
 * del lobby del vr-app (`vr-app/src/ui/Leaderboard.tsx`, rediseñado el
 * 2026-09-07). Tres bandas sobre la misma grilla de columnas:
 *
 *   1. **Podio (1–3)**: filas altas, badge circular de puesto en color medalla
 *      (oro/plata/bronce derivados de la paleta, nunca emojis) y tinte del
 *      mismo color al fondo.
 *   2. **Top 10 (4–10)**: banda hundida (`surface`), hairlines entre filas y
 *      puesto en mono muted — se lee como bloque aparte del podio.
 *   3. **El jugador**: su fila resaltada con el accent del club (barra
 *      izquierda + tinte + chip "Vos"). Fuera del top se repite abajo tras un
 *      separador punteado, para que siempre vea su puesto.
 *
 * El pie dice "Tu puesto: #k de N" con el `me` / `total` de
 * `know.ranking(period, limit, scenarioType)`.
 */
export function RankingSection({
  userId,
  onLoaded,
}: {
  userId?: number
  /** Se llama con el DTO cada vez que llega: la cabecera de `/data` muestra
   *  "#k de N" arriba de todo sin pedir el ranking una segunda vez. Tiene que
   *  ser estable (`useCallback`) — está en las deps del efecto. */
  onLoaded?: (data: RankingDto) => void
}) {
  const [period, setPeriod] = useState<RankingPeriod>('all_time')
  const [data, setData] = useState<RankingDto | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setData(null)
    setError(null)
    know
      .ranking(period, 10)
      .then((r) => {
        if (cancelled) return
        setData(r)
        onLoaded?.(r)
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Error de red.')
      })
    return () => {
      cancelled = true
    }
  }, [period, onLoaded])

  const me = data?.me ?? null
  const entries = data?.entries ?? null
  const podium = entries?.slice(0, 3) ?? []
  const rest = entries?.slice(3) ?? []

  return (
    <section id="ranking">
      <div className="mb-md flex flex-wrap items-center justify-between gap-sm">
        <h2 className="m-0">Ranking global</h2>
        <div className="flex gap-xs">
          {PERIOD_OPTIONS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPeriod(p.value)}
              className={
                p.value === period
                  ? 'btn-primary px-md py-xs text-xs'
                  : 'btn px-md py-xs text-xs'
              }
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="card border-primary/30">
        {error ? (
          <p className="m-0 text-sm text-text-muted">
            No se pudo cargar el ranking ({error}).
          </p>
        ) : entries === null ? (
          <p className="m-0 text-sm text-text-muted">Cargando…</p>
        ) : entries.length === 0 ? (
          <p className="m-0 text-sm text-text-muted">
            Todavía no hay sesiones cerradas en este período — cerrá una para
            aparecer acá.
          </p>
        ) : (
          <div className="flex flex-col gap-sm">
            <div className={`${GRID} px-sm`}>
              <span className={HEAD}>#</span>
              <span className={HEAD}>Jugador</span>
              <span className={`${HEAD} text-right`}>Tiros</span>
              <span className={`${HEAD} text-right`}>Ses.</span>
              <span className={`${HEAD} text-right`}>Score</span>
            </div>

            {podium.length > 0 && (
              <div className="flex flex-col gap-xs">
                {podium.map((e) => (
                  <RankRow
                    key={e.user_id}
                    variant="podium"
                    rank={e.rank}
                    name={e.display_name}
                    nTrials={e.n_trials}
                    nSessions={e.n_sessions}
                    score={e.score}
                    medal={MEDALS[e.rank - 1]}
                    mine={e.user_id === userId}
                  />
                ))}
              </div>
            )}

            {rest.length > 0 && (
              <div className="overflow-hidden rounded-sm border border-outline bg-surface/70">
                {rest.map((e, i) => (
                  <div
                    key={e.user_id}
                    className={i === 0 ? undefined : 'border-t border-outline/60'}
                  >
                    <RankRow
                      variant="list"
                      rank={e.rank}
                      name={e.display_name}
                      nTrials={e.n_trials}
                      nSessions={e.n_sessions}
                      score={e.score}
                      mine={e.user_id === userId}
                    />
                  </div>
                ))}
              </div>
            )}

            {me && !me.in_top && <OutsideTopRow me={me} />}

            <p className="m-0 mt-xs text-center text-sm text-text-secondary">
              {me ? (
                <>
                  Tu puesto:{' '}
                  <strong className="font-bold" style={{ color: CLUB }}>
                    #{me.rank}
                  </strong>
                  {data && data.total > 0 ? ` de ${data.total}` : null}
                </>
              ) : (
                'Todavía no entrás en este ranking — cerrá una sesión para aparecer.'
              )}
            </p>
          </div>
        )}
      </div>
    </section>
  )
}

/**
 * Fila del jugador cuando quedó fuera del top: separada del bloque por un
 * hueco punteado, para que se lea como "…y más abajo, vos" y no como el
 * puesto siguiente al último listado.
 */
function OutsideTopRow({ me }: { me: RankingMe }) {
  return (
    <div className="flex flex-col gap-xs">
      <div className="border-t border-dashed border-outline pt-xs text-center text-xs tracking-wide text-text-muted">
        ···
      </div>
      <div
        className="overflow-hidden rounded-sm border"
        style={{ borderColor: clubTint(0.55) }}
      >
        <RankRow
          variant="list"
          rank={me.rank}
          name="Vos"
          nTrials={me.n_trials}
          nSessions={me.n_sessions}
          score={me.score}
          mine
        />
      </div>
    </div>
  )
}

/**
 * Una fila del ranking. `podium` es la variante alta con badge de medalla;
 * `list` es la compacta del 4 al 10 (y la del jugador fuera del top). `mine`
 * pinta el accent del club encima de cualquiera de las dos.
 */
function RankRow({
  variant,
  rank,
  name,
  nTrials,
  nSessions,
  score,
  medal,
  mine = false,
}: {
  variant: 'podium' | 'list'
  rank: number
  name: string
  nTrials: number
  nSessions: number
  score: number
  medal?: string
  mine?: boolean
}) {
  const isPodium = variant === 'podium'
  const accent = medal ?? palette.textMuted

  return (
    <div
      className={`${GRID} box-border px-sm ${
        isPodium ? 'min-h-[2.75rem] rounded-sm border' : 'min-h-[2.25rem]'
      }`}
      style={{
        background: mine
          ? clubTint(0.16)
          : isPodium
            ? alpha(accent, 0.1)
            : undefined,
        borderColor: isPodium ? (mine ? clubTint(0.55) : alpha(accent, 0.45)) : undefined,
        // Barra izquierda por `inset` y no por `border-left`: un borde real
        // correría las columnas y rompería la alineación entre bandas.
        boxShadow: mine ? `inset 3px 0 0 0 ${CLUB}` : undefined,
      }}
    >
      {isPodium ? (
        <span
          className="inline-flex h-6 w-6 items-center justify-center rounded-full border font-mono text-sm font-bold leading-none tabular-nums"
          style={{
            background: alpha(accent, 0.22),
            borderColor: accent,
            color: accent,
          }}
        >
          {rank}
        </span>
      ) : (
        <span
          className={`font-mono text-sm tabular-nums ${
            mine ? 'text-text-primary' : 'text-text-muted'
          }`}
        >
          {rank}
        </span>
      )}

      <span className="flex min-w-0 items-center gap-xs overflow-hidden">
        <span
          className={`truncate ${
            isPodium
              ? 'text-base font-bold text-text-primary'
              : mine
                ? 'text-sm text-text-primary'
                : 'text-sm text-text-secondary'
          }`}
        >
          {name}
        </span>
        {mine && <MeChip />}
      </span>

      <span className={`${NUM} text-text-secondary`}>{nTrials}</span>
      <span className={`${NUM} text-text-secondary`}>{nSessions}</span>
      <span className={`${NUM} font-medium text-text-primary`}>{score.toFixed(2)}</span>
    </div>
  )
}

function MeChip() {
  return (
    <span
      className="shrink-0 rounded-full border px-xs py-[1px] text-xs uppercase tracking-wide leading-tight whitespace-nowrap"
      style={{ color: CLUB, borderColor: clubTint(0.6) }}
    >
      Vos
    </span>
  )
}
